(() => {
  if (customElements.get('ots-product-question')) return;

  const ENDPOINT = '/apps/ots-offers/product-questions';
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'textarea:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  class OtsProductQuestion extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.openButton = this.querySelector('[data-product-question-open]');
      this.dialog = this.querySelector('[data-product-question-dialog]');
      this.form = this.querySelector('[data-product-question-form]');
      this.formState = this.querySelector('[data-product-question-form-state]');
      this.successState = this.querySelector('[data-product-question-success]');
      this.status = this.querySelector('[data-product-question-status]');
      this.submitButton = this.querySelector('[data-product-question-submit]');
      this.lastFocusedElement = null;
      this.submissionId = null;
      this.variantChangeUnsubscriber = null;

      if (!this.openButton || !this.dialog || !this.form) return;

      this.openButton.addEventListener('click', () => this.open());
      this.querySelectorAll('[data-product-question-close]').forEach((button) => {
        button.addEventListener('click', () => this.close());
      });
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });
      this.dialog.addEventListener('keydown', (event) => this.handleDialogKeydown(event));
      this.dialog.addEventListener('close', () => this.afterClose());
      this.form.addEventListener('submit', (event) => this.submit(event));

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (String(event?.data?.sectionId) !== String(this.dataset.sectionId)) return;
          const variantId = event?.data?.variant?.id;
          if (variantId) this.dataset.variantId = String(variantId);
          else delete this.dataset.variantId;
          const sku = typeof event?.data?.variant?.sku === 'string' ? event.data.variant.sku : '';
          this.dataset.variantSku = sku;
          const skuRow = this.querySelector('[data-product-question-sku]');
          const skuValue = this.querySelector('[data-product-question-sku-value]');
          if (skuRow && skuValue) {
            skuValue.textContent = sku;
            skuRow.hidden = !sku;
          }
        });
      }
    }

    disconnectedCallback() {
      this.variantChangeUnsubscriber?.();
      if (this.dialog?.open) {
        this.dialog.close();
        document.body.classList.remove('ots-product-question-modal-open');
      }
    }

    open() {
      if (this.dialog.open) return;
      this.lastFocusedElement = document.activeElement;
      this.dialog.showModal();
      document.body.classList.add('ots-product-question-modal-open');
      requestAnimationFrame(() => {
        const target =
          this.dialog.querySelector('[data-product-question-success]:not([hidden]) [tabindex="-1"]') ||
          (!this.formState.hidden && this.form.querySelector('input:not([type="hidden"])')) ||
          this.dialog.querySelector('[data-product-question-close]');
        target?.focus();
      });
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    afterClose() {
      document.body.classList.remove('ots-product-question-modal-open');
      if (this.lastFocusedElement instanceof HTMLElement && document.contains(this.lastFocusedElement)) {
        this.lastFocusedElement.focus();
      }
    }

    handleDialogKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = [...this.dialog.querySelectorAll(FOCUSABLE_SELECTOR)].filter(
        (element) => !element.hidden && element.getAttribute('aria-hidden') !== 'true' && element.offsetParent !== null,
      );
      if (!focusable.length) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    clearErrors() {
      this.form.querySelectorAll('[aria-invalid="true"]').forEach((field) => field.removeAttribute('aria-invalid'));
      this.form.querySelectorAll('[data-error-for]').forEach((error) => {
        error.hidden = true;
        error.textContent = '';
      });
      this.status.textContent = '';
    }

    showFieldError(name, message) {
      const field = this.form.elements.namedItem(name);
      const error = this.form.querySelector(`[data-error-for="${CSS.escape(name)}"]`);
      if (field instanceof HTMLElement) field.setAttribute('aria-invalid', 'true');
      if (error) {
        error.textContent = message;
        error.hidden = false;
      }
    }

    validate() {
      this.clearErrors();
      const name = this.form.elements.name.value.trim();
      const email = this.form.elements.email.value.trim();
      const question = this.form.elements.question.value.trim();
      let valid = true;

      if (!name || name.length > 120) {
        this.showFieldError('name', 'Enter your name.');
        valid = false;
      }
      if (!email || email.length > 254 || !EMAIL_PATTERN.test(email)) {
        this.showFieldError('email', 'Enter a valid email address.');
        valid = false;
      }
      if (question.length < 5 || question.length > 4000) {
        this.showFieldError('question', 'Enter a question between 5 and 4,000 characters.');
        valid = false;
      }

      if (!valid) this.form.querySelector('[aria-invalid="true"]')?.focus();
      return valid ? { name, email, question } : null;
    }

    currentVariantId() {
      const productInfo = this.closest('product-info');
      const currentInput = productInfo?.querySelector(
        'form[data-type="add-to-cart-form"] input.product-variant-id, form[data-type="add-to-cart-form"] input[name="id"]',
      );
      return currentInput?.value || this.dataset.variantId || '';
    }

    async submit(event) {
      event.preventDefault();
      if (this.submitButton.disabled) return;
      const values = this.validate();
      if (!values) return;

      const productId = this.dataset.productId || '';
      const variantId = this.currentVariantId();
      if (!productId || !variantId) {
        this.status.textContent = 'This product could not be identified. Refresh the page and try again.';
        return;
      }

      this.submissionId ||= createUuid();
      this.setBusy(true);
      try {
        const response = await fetch(ENDPOINT, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            productId,
            variantId,
            name: values.name,
            email: values.email,
            question: values.question,
            honeypot: this.form.elements.honeypot.value,
            submissionId: this.submissionId,
          }),
        });
        const result = await response.json().catch(() => null);
        if (!response.ok || result?.ok !== true) {
          this.applyServerErrors(result, response.status);
          return;
        }

        this.submissionId = null;
        this.form.reset();
        this.formState.hidden = true;
        this.successState.hidden = false;
        requestAnimationFrame(() => this.querySelector('[data-product-question-success-heading]')?.focus());
      } catch {
        this.status.textContent = 'We could not send your question. Check your connection and try again.';
      } finally {
        this.setBusy(false);
      }
    }

    applyServerErrors(result, statusCode) {
      const fieldErrors = result && typeof result.fieldErrors === 'object' ? result.fieldErrors : {};
      ['name', 'email', 'question'].forEach((name) => {
        if (typeof fieldErrors[name] === 'string') this.showFieldError(name, fieldErrors[name]);
      });
      this.status.textContent =
        typeof result?.message === 'string'
          ? result.message
          : statusCode === 429
            ? 'Please wait before sending another question.'
            : 'We could not send your question. Review your information and try again.';
      this.form.querySelector('[aria-invalid="true"]')?.focus();
    }

    setBusy(isBusy) {
      this.submitButton.disabled = isBusy;
      this.submitButton.classList.toggle('is-loading', isBusy);
      this.form.setAttribute('aria-busy', String(isBusy));
    }
  }

  function createUuid() {
    if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  customElements.define('ots-product-question', OtsProductQuestion);
})();
